import {
  getAccessToken, tryRefresh, clearTokens,
  REFRESHED, IDENTITY_RESTORED,
} from './auth.js';
import { recordFailedRequest } from '../utils/diagnosticsBuffer.js';

// Relative URL — Vite proxies /api/* to the FastAPI backend.
const BASE = '/api';

// Public (account-free) share-link mode: when set, every authFetch carries the
// X-Share-Token header so read endpoints authorize via the link instead of a login.
let shareToken = null;
export function setShareToken(token) { shareToken = token || null; }
export function isPublicMode() { return shareToken != null; }

async function authFetch(url, options = {}) {
  const token = getAccessToken();
  const headers = { ...options.headers };
  // In public (share-link) mode the X-Share-Token is the sole authority for reads.
  // Crucially we do NOT also send Authorization: the backend's get_viewer prefers a
  // bearer over the share token, so a logged-in viewer who lacks independent access
  // to *this* session would be 403'd. Letting the share token stand keeps the
  // read-only view working for logged-in and anonymous viewers alike. (Owners are
  // routed to the full editable view before public mode is ever entered.)
  if (shareToken) {
    headers['X-Share-Token'] = shareToken;
  } else if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  let res = await fetch(url, { ...options, headers });

  if (res.status === 401) {
    // In public mode there is no login session to refresh — surface the error
    // instead of bouncing the anonymous viewer to the login page.
    if (shareToken) {
      throw new Error('This shared link is no longer available');
    }
    const refreshResult = await tryRefresh();
    if (refreshResult === REFRESHED) {
      headers['Authorization'] = `Bearer ${getAccessToken()}`;
      res = await fetch(url, { ...options, headers });
    } else if (refreshResult === IDENTITY_RESTORED) {
      window.location.reload();
      throw new Error('Masquerade session expired — returned to the admin account');
    } else {
      clearTokens();
      window.location.reload();
      throw new Error('Session expired — please log in again');
    }
  }
  // Feed the issue-report diagnostics: method, path and status only.
  if (!res.ok) recordFailedRequest(options.method, url, res.status);
  return res;
}

function uploadWithProgress(url, file, onProgress, { force = false, sidecar = null, recipientEmail = null } = {}) {
  const params = [];
  if (force) params.push('force=true');
  if (recipientEmail) params.push(`recipient_email=${encodeURIComponent(recipientEmail)}`);
  const fullUrl = params.length ? `${url}?${params.join('&')}` : url;
  return new Promise((resolve, reject) => {
    let retried = false;

    function send(token) {
      const form = new FormData();
      form.append('file', file);
      if (sidecar) form.append('sidecar', sidecar);
      const xhr = new XMLHttpRequest();
      xhr.open('POST', fullUrl);
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
      };

      xhr.onload = () => {
        if (xhr.status === 401 && !retried) {
          retried = true;
          tryRefresh().then(refreshResult => {
            if (refreshResult === REFRESHED) { send(getAccessToken()); }
            else if (refreshResult === IDENTITY_RESTORED) {
              window.location.reload();
              reject(new Error('Masquerade session expired — returned to the admin account'));
            } else { clearTokens(); window.location.reload(); reject(new Error('Session expired')); }
          });
          return;
        }
        if (xhr.status === 409) {
          try {
            const body = JSON.parse(xhr.responseText);
            const detail = body.detail || body;
            const err = new Error(detail.message || 'This file has already been uploaded.');
            err.isDuplicate = true;
            err.existingSessionId = detail.existing_session_id;
            err.existingSessionName = detail.existing_session_name;
            reject(err);
          } catch {
            reject(new Error('This file has already been uploaded.'));
          }
          return;
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          try { resolve(JSON.parse(xhr.responseText)); }
          catch { reject(new Error('Invalid response from server')); }
        } else {
          reject(new Error(xhr.responseText || `Upload failed (${xhr.status})`));
        }
      };

      xhr.onerror = () => reject(new Error('Upload failed — network error'));
      xhr.send(form);
    }

    send(getAccessToken());
  });
}

export function uploadCsv(file, onProgress, opts) {
  return uploadWithProgress(`${BASE}/upload`, file, onProgress, opts);
}

// No-signup guest sandbox upload (telemetry only). Returns the standard load
// payload incl. session_id, which the caller registers as the read token.
export function demoUpload(file, onProgress) {
  return uploadWithProgress(`${BASE}/demo/upload`, file, onProgress);
}

export function uploadVideo(file, onProgress, opts) {
  return uploadWithProgress(`${BASE}/upload/video`, file, onProgress, opts);
}

export function uploadXrk(file, onProgress, opts) {
  return uploadWithProgress(`${BASE}/upload/xrk`, file, onProgress, opts);
}

// MoTeC .ld upload. opts may include { force, sidecar } where sidecar is the
// optional matching .ldx File (metadata enrichment; the .ld alone is sufficient).
export function uploadMotec(file, onProgress, opts) {
  return uploadWithProgress(`${BASE}/upload/motec`, file, onProgress, opts);
}

// SmartyCam3 .mp4/.mov upload. The server extracts the embedded AiM telemetry
// data stream as an XRK and ingests it like a native XRK file (video discarded).
export function uploadSmartycam3(file, onProgress, opts) {
  return uploadWithProgress(`${BASE}/upload/smartycam3`, file, onProgress, opts);
}

// ── Upload anything ────────────────────────────────────────────────────────

async function responseError(response, fallback) {
  const body = await response.json().catch(() => null);
  const detail = body?.detail;
  return new Error(typeof detail === 'string' ? detail : fallback);
}

export async function createUploadAnythingBatch(fileCount, totalBytes) {
  const response = await authFetch(`${BASE}/upload-anything`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      expected_file_count: fileCount,
      expected_total_bytes: totalBytes,
    }),
  });
  if (!response.ok) throw await responseError(response, 'Could not create upload directory');
  return response.json();
}

export const UPLOAD_ANYTHING_STALL_TIMEOUT_MS = 120_000;
export const UPLOAD_ANYTHING_MAX_ATTEMPTS = 3;
const UPLOAD_ANYTHING_RETRY_DELAY_MS = 1_000;

export function uploadAnythingFile(
  batchId,
  relativePath,
  file,
  onProgress,
  signal,
  {
    stallTimeoutMs = UPLOAD_ANYTHING_STALL_TIMEOUT_MS,
    maxAttempts = UPLOAD_ANYTHING_MAX_ATTEMPTS,
    retryDelayMs = UPLOAD_ANYTHING_RETRY_DELAY_MS,
    onRetry,
  } = {},
) {
  return new Promise((resolve, reject) => {
    let attempt = 0;
    let authRetriedForAttempt = false;
    let xhr = null;
    let retryTimer = null;
    let stallTimer = null;
    let settled = false;

    const abortError = () => new DOMException('Upload canceled', 'AbortError');

    const clearRetryTimer = () => {
      if (retryTimer !== null) clearTimeout(retryTimer);
      retryTimer = null;
    };

    const clearStallTimer = () => {
      if (stallTimer !== null) clearTimeout(stallTimer);
      stallTimer = null;
    };

    const abort = () => {
      if (settled) return;
      clearRetryTimer();
      clearStallTimer();
      if (xhr) xhr.abort();
      else finish(reject, abortError());
    };
    signal?.addEventListener('abort', abort);

    const finish = (callback, value) => {
      if (settled) return;
      settled = true;
      clearRetryTimer();
      clearStallTimer();
      xhr = null;
      signal?.removeEventListener('abort', abort);
      callback(value);
    };

    const retryOrReject = (error) => {
      if (signal?.aborted) {
        finish(reject, abortError());
        return;
      }
      if (attempt >= maxAttempts) {
        finish(reject, new Error(`${error.message} after ${attempt} attempts`));
        return;
      }
      const delay = retryDelayMs * (2 ** Math.max(0, attempt - 1));
      retryTimer = setTimeout(() => {
        retryTimer = null;
        send(getAccessToken(), true);
      }, delay);
      onProgress?.(0, file.size);
      if (settled) return;
      onRetry?.({
        attempt: attempt + 1,
        maxAttempts,
        reason: error.message,
        delayMs: delay,
      });
    };

    function send(token, newAttempt = false) {
      if (settled) return;
      if (signal?.aborted) {
        finish(reject, abortError());
        return;
      }
      if (newAttempt) {
        attempt += 1;
        authRetriedForAttempt = false;
      }

      const request = new XMLHttpRequest();
      xhr = request;
      let lastLoaded = -1;
      let stalled = false;

      const armStallTimer = () => {
        clearStallTimer();
        stallTimer = setTimeout(() => {
          if (settled || xhr !== request) return;
          stalled = true;
          request.abort();
        }, stallTimeoutMs);
      };

      const releaseRequest = () => {
        if (xhr !== request) return false;
        clearStallTimer();
        xhr = null;
        return true;
      };

      request.open('PUT', `${BASE}/upload-anything/${encodeURIComponent(batchId)}/files`);
      request.setRequestHeader('Authorization', `Bearer ${token}`);
      request.setRequestHeader('Content-Type', 'application/octet-stream');
      request.setRequestHeader('X-Upload-Path', encodeURIComponent(relativePath));
      request.upload.onprogress = event => {
        if (event.loaded > lastLoaded) {
          lastLoaded = event.loaded;
          armStallTimer();
        }
        onProgress?.(event.loaded, event.total || file.size);
      };
      request.onload = () => {
        if (!releaseRequest()) return;
        if (request.status === 401 && !authRetriedForAttempt) {
          authRetriedForAttempt = true;
          tryRefresh().then(refreshResult => {
            if (refreshResult === REFRESHED) send(getAccessToken());
            else if (refreshResult === IDENTITY_RESTORED) {
              window.location.reload();
              finish(reject, new Error('Masquerade session expired — returned to the admin account'));
            } else {
              clearTokens();
              window.location.reload();
              finish(reject, new Error('Session expired'));
            }
          });
          return;
        }
        if (request.status >= 200 && request.status < 300) {
          try { finish(resolve, JSON.parse(request.responseText)); }
          catch { finish(reject, new Error('Invalid response from server')); }
          return;
        }
        let detail = `Upload failed (${request.status})`;
        try { detail = JSON.parse(request.responseText)?.detail || detail; } catch { /* use fallback */ }
        finish(reject, new Error(detail));
      };
      request.onerror = () => {
        if (!releaseRequest()) return;
        retryOrReject(new Error('Upload failed — network error'));
      };
      request.onabort = () => {
        if (!releaseRequest()) return;
        if (stalled) {
          const seconds = Math.ceil(stallTimeoutMs / 1_000);
          retryOrReject(new Error(`Upload stalled — no progress for ${seconds} seconds`));
        } else {
          finish(reject, abortError());
        }
      };
      armStallTimer();
      request.send(file);
    }

    send(getAccessToken(), true);
  });
}

export async function completeUploadAnythingBatch(batchId) {
  const response = await authFetch(
    `${BASE}/upload-anything/${encodeURIComponent(batchId)}/complete`,
    { method: 'POST' },
  );
  if (!response.ok) throw await responseError(response, 'Could not finalize upload');
  return response.json();
}

export async function listUploadAnythingBatches() {
  const response = await authFetch(`${BASE}/upload-anything`);
  if (!response.ok) throw await responseError(response, 'Could not load your uploads');
  return response.json();
}

export async function listUploadAnythingFiles(batchId) {
  const response = await authFetch(
    `${BASE}/upload-anything/${encodeURIComponent(batchId)}/files`,
  );
  if (!response.ok) throw await responseError(response, 'Could not load uploaded files');
  return response.json();
}

export async function downloadUploadAnythingFile(batchId, relativePath) {
  const params = new URLSearchParams({ path: relativePath });
  const response = await authFetch(
    `${BASE}/upload-anything/${encodeURIComponent(batchId)}/download?${params}`,
  );
  if (!response.ok) throw await responseError(response, 'Could not download this file');

  const blobUrl = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = relativePath.split('/').at(-1) || 'download';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
}

export async function deleteUploadAnythingFile(batchId, relativePath) {
  const params = new URLSearchParams({ path: relativePath });
  const response = await authFetch(
    `${BASE}/upload-anything/${encodeURIComponent(batchId)}/files?${params}`,
    { method: 'DELETE' },
  );
  if (!response.ok) throw await responseError(response, 'Could not delete this file');
  return response.json();
}

export async function deleteUploadAnythingFiles(batchId, paths) {
  const response = await authFetch(
    `${BASE}/upload-anything/${encodeURIComponent(batchId)}/files/delete`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ paths }),
    },
  );
  if (!response.ok) throw await responseError(response, 'Could not delete selected files');
  return response.json();
}

export async function deleteUploadAnythingBatch(batchId) {
  const response = await authFetch(
    `${BASE}/upload-anything/${encodeURIComponent(batchId)}`,
    { method: 'DELETE' },
  );
  if (!response.ok) throw await responseError(response, 'Could not delete this upload');
}

// ── ECU merge ──────────────────────────────────────────────────────────────

// Preview a merge from either a File or a log already staged by ecuMatch.
// Pass { stagingId, sourceFilename } to skip re-uploading — a MaxxECU package
// is ~1 MB and the browser already sent it once to get the match.
export function ecuPreview(sessionId, fileOrStaged, onProgress) {
  const url = `${BASE}/sessions/${sessionId}/ecu/preview`;
  if (fileOrStaged instanceof File) {
    // Reuses the XHR upload helper for byte-level progress. onProgress reports
    // 0–100 during transfer; the server then aligns before the response arrives.
    return uploadWithProgress(url, fileOrStaged, onProgress);
  }
  const { stagingId, sourceFilename } = fileOrStaged || {};
  const form = new FormData();
  form.append('staging_id', stagingId);
  if (sourceFilename) form.append('source_filename', sourceFilename);
  return authFetch(url, { method: 'POST', body: form }).then(async res => {
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  });
}

// Upload 1–N ECU logs with no session chosen and get each one's ranked
// candidate sessions. opts: { vehicle, dateFrom, dateTo } narrow the search.
export function ecuMatch(files, opts = {}, onProgress) {
  return new Promise((resolve, reject) => {
    let retried = false;

    function send(token) {
      const form = new FormData();
      Array.from(files).forEach(f => form.append('files', f));
      if (opts.vehicle) form.append('vehicle', opts.vehicle);
      if (opts.dateFrom) form.append('date_from', opts.dateFrom);
      if (opts.dateTo) form.append('date_to', opts.dateTo);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${BASE}/ecu/match`);
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
      xhr.onload = () => {
        if (xhr.status === 401 && !retried) {
          retried = true;
          tryRefresh().then(r => {
            if (r === REFRESHED) send(getAccessToken());
            else { clearTokens(); window.location.reload(); reject(new Error('Session expired')); }
          });
          return;
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          try { resolve(JSON.parse(xhr.responseText)); }
          catch { reject(new Error('Invalid response from server')); }
        } else {
          reject(new Error(xhr.responseText || `Match failed (${xhr.status})`));
        }
      };
      xhr.onerror = () => reject(new Error('Match failed — network error'));
      xhr.send(form);
    }

    send(getAccessToken());
  });
}

// URL of the original upload behind a merge (auth goes via the fetch below).
export async function downloadEcuSource(sessionId, mergeId, filename) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/ecu/${mergeId}/source`);
  if (!res.ok) throw new Error(await res.text());
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'ecu-log';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function ecuCommit(sessionId, body) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/ecu/commit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function listEcuMerges(sessionId) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/ecu`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function removeEcuMerge(sessionId, mergeId) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/ecu/${mergeId}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getLapTrace(sessionId, lapIndex) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/laps/${lapIndex}/trace`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getChannelTrace(sessionId, lapIndex, channelName) {
  const params = new URLSearchParams({ channel: channelName });
  const res = await authFetch(`${BASE}/sessions/${sessionId}/laps/${lapIndex}/channel_trace?${params}`);
  if (!res.ok) {
    // Carry the status so callers can tell "this channel isn't in this
    // session" (404, permanent) from a transient failure worth retrying.
    const err = new Error(await res.text());
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export async function getSessionLaps(sessionId) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/laps`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getTracks() {
  const res = await fetch(`${BASE}/tracks`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// Public aggregate platform stats (laps / sessions / tracks). Returns null on
// failure so callers can simply hide the stats UI.
export async function getGlobalStats() {
  try {
    const res = await fetch(`${BASE}/stats/global`);
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

/* ── Public track pages (/tracks/<slug>) ──────────────────────────────────
   Deliberately plain `fetch`, never authFetch. These pages are served to
   anonymous visitors, and authFetch's 401 path clears tokens and reloads the
   window — on a page with no login to recover, that is an endless reload. A
   bare fetch turns the same failure into an error the page can render. */

export async function getPublicTracks() {
  const res = await fetch(`${BASE}/public/tracks`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getPublicTrack(slug) {
  const res = await fetch(`${BASE}/public/tracks/${encodeURIComponent(slug)}`);
  if (!res.ok) {
    if (res.status === 404) return null;
    throw new Error(await res.text());
  }
  return res.json();
}

/**
 * The 3D lap document + its scenery bake, in one request.
 *
 * Returns null rather than throwing when the venue has no warm bake (404): the
 * server refuses to bake for an anonymous visitor, and the page's answer to
 * that is to show the map without the 3D lap, not an error.
 */
export async function getPublicTrackLap3D(slug) {
  const res = await fetch(`${BASE}/public/tracks/${encodeURIComponent(slug)}/lap3d`);
  if (!res.ok) return null;
  return res.json();
}

export async function getTrack(trackId) {
  const res = await fetch(`${BASE}/tracks/${encodeURIComponent(trackId)}`);
  if (!res.ok) {
    if (res.status === 404) return null;
    throw new Error(await res.text());
  }
  return res.json();
}

export async function saveTrack(trackDef) {
  // authFetch: corner edits are admin-gated server-side — the token must go
  // along or the backend keeps the stored corners.
  const res = await authFetch(`${BASE}/tracks`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(trackDef),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function updateTrack(trackId, trackDef) {
  const res = await fetch(`${BASE}/tracks/${trackId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(trackDef),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function computeMetrics(sessionId, lapIndices, corners, includeTb = false) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/metrics`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ lap_indices: lapIndices, corners, include_tb: includeTb }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getTheoreticalBest(sessionId, sectors) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/tb`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sectors }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();   // { tb_meta, trace }
}

export async function getSectorTimes(sessionId, sectors) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/sector_times`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sectors }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();  // { sectors, laps, tb_times, tb_total_s }
}

export async function getCapabilities(sessionId) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/capabilities`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();  // { available, absent, tier, tiers_present, available_labels, absent_labels }
}

export async function getSectorMetrics(sessionId, sectors) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/sector_metrics`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sectors }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();  // { sectors, laps, available, speed_unit }
}

export async function buildBriefing(payload) {
  const res = await authFetch(`${BASE}/briefing`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function buildSessionRecap(sessionId, corners, depth = 'standard', reflection = null) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/recap-briefing`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      corners: (corners || []).map(c => ({ id: c.id, label: c.label || c.id })),
      depth,
      ...(reflection ? { reflection } : {}),
    }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// Provenance for a debrief. The backend opens the run when it builds the
// evidence, but the browser is what streams from the model, so only the client
// knows what was actually written and how long it took. Best-effort in both
// directions: a provenance failure must never surface to the driver, so these
// swallow their errors rather than rejecting into the recap flow.
export async function completeDebriefRun(runId, payload) {
  if (!runId) return null;
  try {
    const res = await authFetch(`${BASE}/debrief-runs/${runId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload || {}),
    });
    return res.ok ? res.json() : null;
  } catch {
    return null;
  }
}

export async function submitDebriefFeedback(runId, payload) {
  if (!runId) return null;
  const res = await authFetch(`${BASE}/debrief-runs/${runId}/feedback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload || {}),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// Background reading for a free-text chat question. Never throws and never
// blocks the answer: an empty list means the chat replies from the session
// evidence alone, which is the grounded path anyway.
export async function fetchCoachingGuidance(question, limit = 3) {
  try {
    const res = await authFetch(`${BASE}/coaching/guidance`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ question, limit }),
    });
    if (!res.ok) return [];
    const data = await res.json();
    return data?.passages || [];
  } catch {
    return [];
  }
}

export async function updateCorner(trackId, cornerId, cornerData) {
  const res = await authFetch(`${BASE}/tracks/${trackId}/corners/${cornerId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(cornerData),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// Used whenever /config can't be reached. The coach_* keys are deliberately
// absent rather than guessed: the callers already carry their own defaults, and
// one missing key is better than two places disagreeing about which model runs.
const EDITION_CAPABILITIES = {
  server: {
    user_accounts: true,
    session_sharing: true,
    public_leaderboards: true,
    cross_session_analytics: true,
    sync_to_server: false,
  },
  desktop: {
    user_accounts: false,
    session_sharing: false,
    public_leaderboards: false,
    cross_session_analytics: false,
    sync_to_server: true,
  },
};

const APP_CONFIG_FALLBACK = {
  edition: 'server',
  capabilities: EDITION_CAPABILITIES.server,
  enable_ai: true,
  enable_ai_debrief: false,
  enable_trace_coaching: false,
};

function normalizeAppConfig(value) {
  const config = value && typeof value === 'object' ? value : {};
  const edition = config.edition === 'desktop' ? 'desktop' : 'server';
  const capabilities = config.capabilities && typeof config.capabilities === 'object'
    ? config.capabilities
    : {};
  return {
    ...APP_CONFIG_FALLBACK,
    ...config,
    edition,
    capabilities: {
      ...EDITION_CAPABILITIES[edition],
      ...capabilities,
    },
  };
}

export async function getAppConfig() {
  // Feature-flag endpoint; sends auth token so admins always get enable_ai=true.
  try {
    const res = await authFetch(`${BASE}/config`);
    if (!res.ok) return { ...APP_CONFIG_FALLBACK };
    return normalizeAppConfig(await res.json());
  } catch {
    return { ...APP_CONFIG_FALLBACK };
  }
}

export async function getDesktopRelease() {
  const res = await authFetch(`${BASE}/desktop-release`);
  if (!res.ok) throw await responseError(res, 'Could not load the Windows beta release');
  return res.json();
}

export async function requestDesktopReleaseDownload() {
  const res = await authFetch(`${BASE}/desktop-release/download-token`, {
    method: 'POST',
  });
  if (!res.ok) throw await responseError(res, 'Could not start the installer download');
  return res.json();
}

export async function snapCorner(sessionId, lat, lon) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/corners/snap`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ lat, lon }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function redetectCorners(sessionId, { targetCount = null, sensitivity = null } = {}) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/corners/redetect`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      target_count: targetCount,
      sensitivity,
    }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

function toQueryString(params) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params || {})) {
    if (v !== null && v !== undefined && v !== '') qs.set(k, v);
  }
  const s = qs.toString();
  return s ? `?${s}` : '';
}

// Paginated, server-filtered Recent Sessions list. Returns { sessions, next_cursor }.
// `params` mirrors the backend's query params directly: q, track, car, driver,
// temp_min, temp_max, condition, date_field, date_from, date_to, tab, sort,
// sort_dir, cursor, page_size, admin_mode (false restricts admins to ordinary access).
export async function listSessions(params = {}) {
  const res = await authFetch(`${BASE}/sessions${toQueryString(params)}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// Distinct track/car/driver options + temp/date bounds for the current filter
// context — replaces client-side faceting over the full fetched array.
export async function getSessionFacets(params = {}) {
  const res = await authFetch(`${BASE}/sessions/facets${toQueryString(params)}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// Career summary (laps/sessions/tracks/hours) + per-track best lap, over the
// user's own non-shared-to-them sessions.
export async function getSessionStats() {
  const res = await authFetch(`${BASE}/sessions/stats`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function loadSession(sessionId) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/load`, { method: 'POST' });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function deleteSession(sessionId) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function bulkDeleteSessions(sessionIds) {
  const res = await authFetch(`${BASE}/sessions/bulk-delete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ session_ids: sessionIds }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function patchSessionMeta(sessionId, patch) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/meta`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// Re-segment a session's laps onto its track's canonical start/finish
// (owner or admin only). Lap times change when the timing line moves.
export async function realignSessionSf(sessionId) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/realign_sf`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// Undo a realign: restore the session's original (device) lap boundaries.
export async function unalignSessionSf(sessionId) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/unalign_sf`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function searchUsers(q) {
  if (!q || q.length < 2) return [];
  const res = await authFetch(`${BASE}/users/search?q=${encodeURIComponent(q)}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function shareSession(sessionId, email, permission = 'view') {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/shares`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, permission }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function shareSessionWithAll(sessionId, permission = 'view') {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/shares/all`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ permission }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function listShares(sessionId) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/shares`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function removeShare(sessionId, shareId) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/shares/${shareId}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(await res.text());
}

export async function removeGlobalShare(sessionId) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/global-share`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(await res.text());
}

// ── Follows ───────────────────────────────────────────────────────────────────

export async function followUser(userId) {
  const res = await authFetch(`${BASE}/users/${userId}/follow`, { method: 'POST' });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function unfollowUser(userId) {
  const res = await authFetch(`${BASE}/users/${userId}/follow`, { method: 'DELETE' });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getFollowing() {
  const res = await authFetch(`${BASE}/users/me/following`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getFollowers(userId) {
  const res = await authFetch(`${BASE}/users/${userId}/followers`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// ── Account-free public share links ──────────────────────────────────────────

// Resolve a public link token → full read-only load payload. If a logged-in token
// is present we pass it so the backend can report whether the viewer owns this
// session (is_owner) — the PublicGate uses that to route owners to the full view.
export async function getPublicSession(token) {
  const access = getAccessToken();
  const res = await fetch(`${BASE}/public/${encodeURIComponent(token)}`, {
    headers: access ? { Authorization: `Bearer ${access}` } : {},
  });
  if (!res.ok) {
    const err = new Error(res.status === 404
      ? 'This shared link is no longer available'
      : await res.text());
    err.status = res.status;
    throw err;
  }
  return res.json();
}

// Owner: fetch the current public link, or null if none exists yet.
export async function getPublicLink(sessionId) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/public-link`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// Owner: create (or return the existing) public link.
export async function createPublicLink(sessionId) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/public-link`, { method: 'POST' });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// Owner: revoke the public link (permanently invalidates the token).
export async function deletePublicLink(sessionId) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/public-link`, { method: 'DELETE' });
  if (!res.ok && res.status !== 404) throw new Error(await res.text());
}

// Admin: send a session invite email (creates public link if needed).
export async function sendSessionInvite(sessionId, recipientEmail, subject, body) {
  const res = await authFetch(`${BASE}/admin/sessions/${sessionId}/send-invite`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ recipient_email: recipientEmail, subject, body }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || 'Failed to send invite');
  }
}

export async function listComments(sessionId) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/comments`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function postComment(sessionId, body, annotation = {}) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/comments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ body, ...annotation }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function updateComment(sessionId, commentId, body) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/comments/${commentId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ body }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function deleteComment(sessionId, commentId) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/comments/${commentId}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(await res.text());
}

export async function toggleReaction(sessionId, commentId, emoji) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/comments/${commentId}/reactions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ emoji }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// ── Formulas ────────────────────────────────────────────────────────────────

export async function getFormulas() {
  const res = await authFetch(`${BASE}/formulas`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function createFormula(body) {
  const res = await authFetch(`${BASE}/formulas`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function updateFormula(id, body) {
  const res = await authFetch(`${BASE}/formulas/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function deleteFormula(id) {
  const res = await authFetch(`${BASE}/formulas/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(await res.text());
}

// ── Help articles ──────────────────────────────────────────────────────────

export async function getHelpArticles() {
  const res = await authFetch(`${BASE}/help/articles`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function createHelpArticle(body) {
  const res = await authFetch(`${BASE}/help/articles`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function updateHelpArticle(id, body) {
  const res = await authFetch(`${BASE}/help/articles/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function deleteHelpArticle(id) {
  const res = await authFetch(`${BASE}/help/articles/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(await res.text());
}

export async function uploadHelpAsset(file) {
  const form = new FormData();
  form.append('file', file);
  const res = await authFetch(`${BASE}/help/assets`, { method: 'POST', body: form });
  if (!res.ok) throw new Error(await res.text());
  return res.json(); // { url }
}

export async function getFormulaTrace(sessionId, lapIndex, expression, name) {
  const res = await authFetch(
    `${BASE}/sessions/${sessionId}/laps/${lapIndex}/formula_trace`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ expression, name }),
    }
  );
  if (!res.ok) {
    // 422 returns a structured FormulaError — surface it
    const body = await res.json().catch(() => ({}));
    const msg = body?.detail?.message || body?.detail || 'Formula evaluation failed';
    const err = new Error(msg);
    err.formulaError = body?.detail;
    throw err;
  }
  return res.json();
}

export async function getFormulaAliases(sessionId) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/formula_aliases`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();  // { aliases: string[] }
}

// ── Power (virtual dyno) + vehicle profiles ────────────────────────────────

export async function getPowerTrace(sessionId, lapIndex, vehicleParams) {
  const res = await authFetch(
    `${BASE}/sessions/${sessionId}/laps/${lapIndex}/power_trace`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(vehicleParams),
    }
  );
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getSessionVehicle(sessionId) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/vehicle`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function createVehicle(profile) {
  const res = await authFetch(`${BASE}/vehicles`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(profile),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function updateVehicle(vehicleId, patch) {
  const res = await authFetch(`${BASE}/vehicles/${vehicleId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getUserPreferences() {
  const res = await authFetch(`${BASE}/user/preferences`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function downloadSessionFile(sessionId, type, filename) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/download/${type}`);
  if (!res.ok) throw new Error(await res.text());
  const blob = await res.blob();
  const name = filename || (() => {
    const cd = res.headers.get('content-disposition') || '';
    const match = cd.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    return match ? match[1].replace(/['"]/g, '') : `session_${sessionId}_${type}`;
  })();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function saveUserPreferences(prefs) {
  const res = await authFetch(`${BASE}/user/preferences`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(prefs),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function deleteSessionVideo(sessionId) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/video`, { method: 'DELETE' });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// ── Video render (VideoTrackRender) ────────────────────────────────────────

export const VIDEO_UPLOAD_STALL_TIMEOUT_MS = 120_000;
export const VIDEO_UPLOAD_FINALIZE_TIMEOUT_MS = 15 * 60_000;
export const VIDEO_UPLOAD_MAX_ATTEMPTS = 3;
const VIDEO_UPLOAD_RETRY_DELAY_MS = 1_000;

function videoUploadResponseError(request, fallback) {
  try {
    const body = JSON.parse(request.responseText);
    const detail = body?.detail;
    return new Error(typeof detail === 'string' ? detail : fallback);
  } catch {
    return new Error(request.responseText || fallback);
  }
}

function uploadGoproFile(sessionId, file, onProgress, options) {
  return streamVideoPut(`${BASE}/sessions/${sessionId}/render/gopro_file`, file, onProgress, options);
}

// Stream one video as a raw PUT body with stall detection and retries.
//
// The stall timer re-arms on upload progress, so it cannot govern the wait
// after the last byte: the staging endpoint fingerprints the file before it
// answers (a demux of the whole file, minutes for a big GoPro chapter), and
// aborting there would re-upload the file from scratch. Once the body is
// fully sent the wait switches to `finalizeTimeoutMs`.
function streamVideoPut(
  url,
  file,
  onProgress,
  {
    stallTimeoutMs = VIDEO_UPLOAD_STALL_TIMEOUT_MS,
    finalizeTimeoutMs = VIDEO_UPLOAD_FINALIZE_TIMEOUT_MS,
    maxAttempts = VIDEO_UPLOAD_MAX_ATTEMPTS,
    retryDelayMs = VIDEO_UPLOAD_RETRY_DELAY_MS,
  } = {},
) {
  return new Promise((resolve, reject) => {
    let attempt = 0;
    let authRetriedForAttempt = false;
    let request = null;
    let retryTimer = null;
    let stallTimer = null;
    let settled = false;

    const clearRetryTimer = () => {
      if (retryTimer !== null) clearTimeout(retryTimer);
      retryTimer = null;
    };
    const clearStallTimer = () => {
      if (stallTimer !== null) clearTimeout(stallTimer);
      stallTimer = null;
    };
    const finish = (callback, value) => {
      if (settled) return;
      settled = true;
      clearRetryTimer();
      clearStallTimer();
      request = null;
      callback(value);
    };
    const retryOrReject = error => {
      if (attempt >= maxAttempts) {
        finish(reject, new Error(`${error.message} after ${attempt} attempts`));
        return;
      }
      const delayMs = retryDelayMs * (2 ** Math.max(0, attempt - 1));
      onProgress?.(0, {
        status: 'retrying',
        attempt: attempt + 1,
        maxAttempts,
        reason: error.message,
        delayMs,
      });
      retryTimer = setTimeout(() => {
        retryTimer = null;
        send(getAccessToken(), true);
      }, delayMs);
    };

    function send(token, newAttempt) {
      if (settled) return;
      if (newAttempt) {
        attempt += 1;
        authRetriedForAttempt = false;
      }

      const xhr = new XMLHttpRequest();
      request = xhr;
      let stalled = false;
      let lastLoaded = -1;

      const armStallTimer = (ms = stallTimeoutMs) => {
        clearStallTimer();
        stallTimer = setTimeout(() => {
          if (settled || request !== xhr) return;
          stalled = true;
          xhr.abort();
        }, ms);
      };
      const releaseRequest = () => {
        if (request !== xhr) return false;
        clearStallTimer();
        request = null;
        return true;
      };

      xhr.open('PUT', url);
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.setRequestHeader('Content-Type', 'application/octet-stream');
      xhr.setRequestHeader('X-Upload-Filename', encodeURIComponent(file.name));
      xhr.setRequestHeader('X-Upload-Size', String(file.size));
      xhr.upload.onprogress = event => {
        if (event.loaded > lastLoaded) {
          lastLoaded = event.loaded;
          armStallTimer(event.loaded >= file.size ? finalizeTimeoutMs : stallTimeoutMs);
        }
        onProgress?.(event.loaded, {
          status: event.loaded >= file.size ? 'finalizing' : 'uploading',
          attempt,
          maxAttempts,
        });
      };
      xhr.onload = () => {
        if (!releaseRequest()) return;
        if (xhr.status === 401 && !authRetriedForAttempt) {
          authRetriedForAttempt = true;
          tryRefresh().then(refreshResult => {
            if (refreshResult === REFRESHED) send(getAccessToken(), false);
            else if (refreshResult === IDENTITY_RESTORED) {
              window.location.reload();
              finish(reject, new Error('Masquerade session expired — returned to the admin account'));
            } else {
              clearTokens();
              window.location.reload();
              finish(reject, new Error('Session expired'));
            }
          });
          return;
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          try { finish(resolve, JSON.parse(xhr.responseText)); }
          catch { finish(reject, new Error('Invalid response from server')); }
          return;
        }
        const error = videoUploadResponseError(xhr, `Upload failed (${xhr.status})`);
        if (xhr.status === 0 || xhr.status === 408 || xhr.status === 429 || xhr.status >= 500) {
          retryOrReject(error);
        } else {
          finish(reject, error);
        }
      };
      xhr.onerror = () => {
        if (!releaseRequest()) return;
        retryOrReject(new Error('Upload interrupted by a network error'));
      };
      xhr.onabort = () => {
        if (!releaseRequest()) return;
        retryOrReject(new Error(stalled
          ? `Upload stalled for ${Math.round(stallTimeoutMs / 1000)} seconds`
          : 'Upload interrupted'));
      };

      armStallTimer();
      xhr.send(file);
    }

    send(getAccessToken(), true);
  });
}

export async function uploadGoproFiles(sessionId, files, onProgress, options) {
  const uploadFiles = [...files];
  const totalBytes = uploadFiles.reduce((sum, file) => sum + file.size, 0);
  let completedBytes = 0;
  const uploaded = [];

  for (let index = 0; index < uploadFiles.length; index += 1) {
    const file = uploadFiles[index];
    const result = await uploadGoproFile(
      sessionId,
      file,
      (fileLoaded, detail) => {
        const loadedBytes = completedBytes + fileLoaded;
        const percent = totalBytes > 0 ? Math.round((loadedBytes / totalBytes) * 100) : 0;
        onProgress?.(percent, {
          ...detail,
          currentFilename: file.name,
          currentFile: index + 1,
          fileCount: uploadFiles.length,
          loadedBytes,
          totalBytes,
        });
      },
      options,
    );
    completedBytes += file.size;
    uploaded.push(...(result.uploaded || [file.name]));
  }

  onProgress?.(100, {
    status: 'complete',
    currentFile: uploadFiles.length,
    fileCount: uploadFiles.length,
    loadedBytes: totalBytes,
    totalBytes,
  });
  return { uploaded, session_id: sessionId };
}

// ── Video → session auto-match (Upload page drops) ──────────────────────────

export function uploadVideoStaging(file, onProgress, options) {
  return streamVideoPut(`${BASE}/video/staging`, file, onProgress, options);
}

export async function videoMatch({ stagingIds, vehicle, dateFrom, dateTo, reprobe } = {}) {
  const res = await authFetch(`${BASE}/video/match`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      staging_ids: stagingIds,
      vehicle: vehicle || null,
      date_from: dateFrom || null,
      date_to: dateTo || null,
      reprobe: !!reprobe,
    }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function attachStagedVideos(sessionId, stagingIds) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/video/attach`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ staging_ids: stagingIds }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function createSessionFromStagedVideo(stagingId, { force = false } = {}) {
  const res = await authFetch(
    `${BASE}/video/staging/${stagingId}/create_session${force ? '?force=true' : ''}`,
    { method: 'POST' },
  );
  if (res.status === 409) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body?.detail?.message || 'This file has already been uploaded.');
    err.isDuplicate = true;
    err.detail = body?.detail;
    throw err;
  }
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function deleteStagedVideo(stagingId) {
  const res = await authFetch(`${BASE}/video/staging/${stagingId}`, { method: 'DELETE' });
  if (!res.ok && res.status !== 404) throw new Error(await res.text());
}

export async function submitRenderJob(sessionId, payload) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/render`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getRenderStatus(sessionId) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/render/status`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function cancelRender(sessionId) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/render/cancel`, { method: 'POST' });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getVtrQueue() {
  const res = await authFetch(`${BASE}/render/queue`);
  if (!res.ok) return [];
  return res.json();
}

export async function getOverlayStyles() {
  const res = await authFetch(`${BASE}/render/overlay_styles`);
  if (!res.ok) return {};
  return res.json();
}

export async function detectRenderSync(sessionId) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/render/sync`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function submitPreviewClip(sessionId, payload) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/render/preview_clip`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getPreviewClipStatus(sessionId, jobId) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/render/preview_clip/${jobId}/status`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getPreviewFrame(sessionId, payload, signal) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/render/preview_frame`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });
  if (!res.ok) throw new Error(await res.text());
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

export async function getPreviewLayers(sessionId, payload, signal) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/render/preview_layers`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });
  if (!res.ok) {
    const message = await res.text();
    let detail;
    try { detail = JSON.parse(message).detail; } catch { /* non-JSON upstream error */ }
    throw new Error(typeof detail === 'string' ? detail : 'Preview could not be updated. Please retry.');
  }
  return res.json();
}

// Lap clips are cut as VTR jobs: submit, poll until download_ready, then fetch.
// `endLapIndex` (inclusive) extends the cut over a contiguous span of laps;
// omitted, it is a single-lap clip.
export async function submitLapClip(sessionId, { lapIndex, endLapIndex, preSeconds, postSeconds }) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/clip`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      lap_index: lapIndex,
      end_lap_index: endLapIndex ?? lapIndex,
      pre_seconds: preSeconds,
      post_seconds: postSeconds,
    }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getLapClipStatus(sessionId, clipId) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/clip/${clipId}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function downloadLapClip(sessionId, clipId) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/clip/${clipId}/download`);
  if (!res.ok) throw new Error(await res.text());
  const blob = await res.blob();
  const disposition = res.headers.get('content-disposition') ?? '';
  const match = disposition.match(/filename="?([^";\n]+)"?/);
  return { blob, filename: match ? match[1] : `clip_${clipId.slice(0, 8)}.mp4` };
}

export async function createVideoComparisonExport(payload) {
  const res = await authFetch(`${BASE}/video-comparison-exports`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// Stage a browser-rendered ghost race for use as a comparison tile. `trace`
// maps distance along the lap to time inside the clip, which is what lets the
// server-side compositor seek the tile to wherever the REF car is in the
// footage. The returned clip_id is passed to createVideoComparisonExport.
export async function uploadComparison3dClip(blob, { fps, frames, container, trace }) {
  const form = new FormData();
  form.append('clip', blob, `ghost.${container}`);
  form.append('fps', String(fps));
  form.append('frames', String(frames));
  form.append('container', container);
  form.append('trace', JSON.stringify(trace));
  const res = await authFetch(`${BASE}/video-comparison-exports/3d-clip`, {
    method: 'POST',
    body: form,
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getVideoComparisonExport(exportId) {
  const res = await authFetch(`${BASE}/video-comparison-exports/${exportId}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function cancelVideoComparisonExport(exportId) {
  const res = await authFetch(`${BASE}/video-comparison-exports/${exportId}`, { method: 'DELETE' });
  if (!res.ok && res.status !== 404) throw new Error(await res.text());
}

export async function downloadVideoComparisonExport(exportId) {
  const res = await authFetch(`${BASE}/video-comparison-exports/${exportId}/download`);
  if (!res.ok) throw new Error(await res.text());
  const blob = await res.blob();
  const disposition = res.headers.get('content-disposition') ?? '';
  const match = disposition.match(/filename="?([^";\n]+)"?/);
  return { blob, filename: match ? match[1] : `lap_comparison_${exportId.slice(0, 8)}.mp4` };
}

// ── Groups ─────────────────────────────────────────────────────────────────

export async function createGroup(name) {
  const res = await authFetch(`${BASE}/groups`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function listGroups() {
  const res = await authFetch(`${BASE}/groups`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function renameGroup(groupId, name) {
  const res = await authFetch(`${BASE}/groups/${groupId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function deleteGroup(groupId) {
  const res = await authFetch(`${BASE}/groups/${groupId}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(await res.text());
}

export async function addGroupMember(groupId, email) {
  const res = await authFetch(`${BASE}/groups/${groupId}/members`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function removeGroupMember(groupId, userId) {
  const res = await authFetch(`${BASE}/groups/${groupId}/members/${userId}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function shareSessionWithGroup(sessionId, groupId, permission = 'view') {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/shares/group`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ group_id: groupId, permission }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function removeGroupShare(sessionId, groupId) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/shares/group/${groupId}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(await res.text());
}

export async function listGroupShares(sessionId) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/shares/groups`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// ── Leaderboard ────────────────────────────────────────────────────────────

// `scope` is 'all' (own + shared-with-you, default) or 'public' (only sessions
// shared with everyone — the board the whole world sees).
export async function getLeaderboardTracks(scope = 'all') {
  const params = new URLSearchParams();
  if (scope && scope !== 'all') params.set('scope', scope);
  const qs = params.toString() ? `?${params}` : '';
  const res = await authFetch(`${BASE}/leaderboard/tracks${qs}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getLeaderboard(trackId, vehicleId = null, scope = 'all') {
  const params = new URLSearchParams({ track_id: trackId });
  if (vehicleId) params.set('vehicle_id', vehicleId);
  if (scope && scope !== 'all') params.set('scope', scope);
  const res = await authFetch(`${BASE}/leaderboard?${params}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// ── Driver analytics (Progress view) ──────────────────────────────────────
// `userId` is optional: admins pass another driver's id to view their Progress;
// omitting it (the default) is the normal self-view.

export async function getCareerOverview(userId = null, vehicleId = null) {
  const params = new URLSearchParams();
  if (userId) params.set('user_id', userId);
  if (vehicleId) params.set('vehicle_id', vehicleId);
  const qs = params.toString() ? `?${params}` : '';
  const res = await authFetch(`${BASE}/analytics/overview${qs}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// All cars the user has driven (any track) — for the career-page car selector.
export async function getUserVehicles(userId = null) {
  const params = new URLSearchParams();
  if (userId) params.set('user_id', userId);
  const qs = params.toString() ? `?${params}` : '';
  const res = await authFetch(`${BASE}/analytics/vehicles${qs}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getUserTrackVehicles(trackId, userId = null) {
  const params = new URLSearchParams();
  if (userId) params.set('user_id', userId);
  const qs = params.toString() ? `?${params}` : '';
  const res = await authFetch(`${BASE}/analytics/track/${encodeURIComponent(trackId)}/vehicles${qs}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getTrackProgression(trackId, vehicleId = null, userId = null, conditions = 'all', tempMinC = null, tempMaxC = null, agg = 'median') {
  const params = new URLSearchParams();
  if (vehicleId) params.set('vehicle_id', vehicleId);
  if (userId) params.set('user_id', userId);
  if (conditions && conditions !== 'all') params.set('conditions', conditions);
  if (tempMinC != null) params.set('temp_min_c', tempMinC);
  if (tempMaxC != null) params.set('temp_max_c', tempMaxC);
  if (agg && agg !== 'median') params.set('agg', agg);
  const qs = params.toString() ? `?${params}` : '';
  const res = await authFetch(`${BASE}/analytics/track/${encodeURIComponent(trackId)}/progression${qs}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getStyleTrend(trackId, vehicleId = null, userId = null) {
  const params = new URLSearchParams();
  if (vehicleId) params.set('vehicle_id', vehicleId);
  if (userId) params.set('user_id', userId);
  const qs = params.toString() ? `?${params}` : '';
  const res = await authFetch(`${BASE}/analytics/track/${encodeURIComponent(trackId)}/style_trend${qs}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getTrackCornerGaps(trackId, vehicleId, userId = null) {
  const params = new URLSearchParams({ vehicle_id: vehicleId });
  if (userId) params.set('user_id', userId);
  const res = await authFetch(`${BASE}/analytics/track/${encodeURIComponent(trackId)}/corners?${params}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getTrackFieldSummary(trackId, vehicleId, userId = null) {
  const params = new URLSearchParams({ vehicle_id: vehicleId });
  if (userId) params.set('user_id', userId);
  const res = await authFetch(`${BASE}/analytics/track/${encodeURIComponent(trackId)}/field_summary?${params}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getTrackCornerTechnique(trackId, cornerId, vehicleId, userId = null) {
  const params = new URLSearchParams({ vehicle_id: vehicleId });
  if (userId) params.set('user_id', userId);
  const res = await authFetch(
    `${BASE}/analytics/track/${encodeURIComponent(trackId)}/corners/${encodeURIComponent(cornerId)}/technique?${params}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getTrackCorners(trackId, vehicleId = null, userId = null) {
  const params = new URLSearchParams();
  if (vehicleId) params.set('vehicle_id', vehicleId);
  if (userId) params.set('user_id', userId);
  const qs = params.toString() ? `?${params}` : '';
  const res = await authFetch(`${BASE}/analytics/track/${encodeURIComponent(trackId)}/corner_list${qs}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getTrackCornerProgression(trackId, cornerId, vehicleId, userId = null, conditions = 'all', tempMinC = null, tempMaxC = null, agg = 'median') {
  const params = new URLSearchParams({ vehicle_id: vehicleId });
  if (userId) params.set('user_id', userId);
  if (conditions && conditions !== 'all') params.set('conditions', conditions);
  if (tempMinC != null) params.set('temp_min_c', tempMinC);
  if (tempMaxC != null) params.set('temp_max_c', tempMaxC);
  if (agg && agg !== 'median') params.set('agg', agg);
  const res = await authFetch(
    `${BASE}/analytics/track/${encodeURIComponent(trackId)}/corners/${encodeURIComponent(cornerId)}/progression?${params}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getTrackCraft(trackId, userId = null) {
  const params = new URLSearchParams();
  if (userId) params.set('user_id', userId);
  const qs = params.toString() ? `?${params}` : '';
  const res = await authFetch(`${BASE}/analytics/track/${encodeURIComponent(trackId)}/craft${qs}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getBenchmarkLap(trackId, vehicleId, userId = null) {
  const params = new URLSearchParams({ vehicle_id: vehicleId });
  if (userId) params.set('user_id', userId);
  const res = await authFetch(`${BASE}/analytics/track/${encodeURIComponent(trackId)}/benchmark?${params}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// ── Channel statistics (descriptive stats over a track + user + vehicle) ───

// Channels logged for a scope, with per-channel session coverage.
export async function getChannelStatChannels(trackId, vehicleId, userId = null) {
  const params = new URLSearchParams({ track_id: trackId, vehicle_id: vehicleId });
  if (userId) params.set('user_id', userId);
  const res = await authFetch(`${BASE}/channel_stats/channels?${params}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// Per-session aggregated stats for the requested channels over a lap basis.
// sessionIds / channels are arrays; histograms are returned only for a single channel.
export async function getChannelStats(trackId, vehicleId, {
  sessionIds = null, channels = null, lapBasis = 'all', userId = null,
} = {}) {
  const params = new URLSearchParams({ track_id: trackId, vehicle_id: vehicleId });
  if (lapBasis && lapBasis !== 'all') params.set('lap_basis', lapBasis);
  if (userId) params.set('user_id', userId);
  (sessionIds || []).forEach(s => params.append('session_ids', s));
  (channels || []).forEach(c => params.append('channels', c));
  const res = await authFetch(`${BASE}/channel_stats?${params}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// ── Session report (sections over a selection of sessions) ────────────────

export async function generateReport(sessionIds, definition) {
  const res = await authFetch(`${BASE}/reports/generate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ session_ids: sessionIds, definition }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getReportChannels(sessionIds) {
  const params = new URLSearchParams();
  (sessionIds || []).forEach(s => params.append('session_ids', s));
  const res = await authFetch(`${BASE}/reports/channels?${params}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function listReportTemplates() {
  const res = await authFetch(`${BASE}/reports/templates`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function createReportTemplate(body) {
  const res = await authFetch(`${BASE}/reports/templates`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function updateReportTemplate(id, body) {
  const res = await authFetch(`${BASE}/reports/templates/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function deleteReportTemplate(id) {
  const res = await authFetch(`${BASE}/reports/templates/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(await res.text());
}

// ── Search page (cross-session sector / point queries) ─────────────────────

// Tracks with searchable laps under a scope ('mine' | 'shared' | 'all').
export async function getSearchTracks(scope = 'all') {
  const res = await authFetch(`${BASE}/search/tracks?scope=${scope}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// Vehicles with searchable laps at a track — drives the car filter.
export async function getSearchVehicles(trackId, scope = 'all') {
  const params = new URLSearchParams({ track_id: trackId, scope });
  const res = await authFetch(`${BASE}/search/vehicles?${params}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// Channels available for point queries at a track, with session coverage.
export async function getSearchChannels(trackId, scope = 'all', vehicleId = null) {
  const params = new URLSearchParams({ track_id: trackId, scope });
  if (vehicleId) params.set('vehicle_id', vehicleId);
  const res = await authFetch(`${BASE}/search/channels?${params}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// The fastest in-scope lap at a track — drives the Search page's trace + map.
export async function getSearchReferenceLap(trackId, scope = 'all', vehicleId = null) {
  const params = new URLSearchParams({ track_id: trackId, scope });
  if (vehicleId) params.set('vehicle_id', vehicleId);
  const res = await authFetch(`${BASE}/search/reference_lap?${params}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// Top-N best sector times per sector across in-scope laps.
export async function searchSectors(body) {
  const res = await authFetch(`${BASE}/search/sectors`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// Top-N laps by a channel's value at a point (fraction of lap distance).
export async function searchPoint(body) {
  const res = await authFetch(`${BASE}/search/point`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// ── Per-user cars (identity + physics) ─────────────────────────────────────

export async function getCars() {
  const res = await authFetch(`${BASE}/cars`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function createCar(body) {
  const res = await authFetch(`${BASE}/cars`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function patchCar(carId, patch) {
  const res = await authFetch(`${BASE}/cars/${encodeURIComponent(carId)}`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function mergeCars(targetCarId, sourceCarId) {
  const res = await authFetch(`${BASE}/cars/${encodeURIComponent(targetCarId)}/merge`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ source_car_id: sourceCarId }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function deleteCar(carId) {
  const res = await authFetch(`${BASE}/cars/${encodeURIComponent(carId)}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// Admin: fold one track's leaderboard into another canonical track.
export async function mergeTracks(sourceTrackId, canonicalTrackId) {
  const res = await authFetch(`${BASE}/leaderboard/tracks/merge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ source_track_id: sourceTrackId, canonical_track_id: canonicalTrackId }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// Return the canonical leaderboard track_id for a session (null if not on a known track).
export async function getSessionLeaderboardTrack(sessionId) {
  const res = await authFetch(`${BASE}/leaderboard/session_track/${encodeURIComponent(sessionId)}`);
  if (!res.ok) return { track_id: null };
  return res.json();
}

// Admin: detach a previously-merged track so it stands on its own again.
export async function unmergeTrack(trackId) {
  const res = await authFetch(`${BASE}/leaderboard/tracks/${encodeURIComponent(trackId)}/unmerge`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// Admin: rename a track's display name (written to both the track definition and
// the DB row, so future uploads keep the new name).
export async function renameLeaderboardTrack(trackId, trackName) {
  const res = await authFetch(`${BASE}/leaderboard/tracks/${encodeURIComponent(trackId)}/rename`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ track_name: trackName }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// Admin: recompute one session's leaderboard entries against an explicit track,
// correcting a GPS mis-attribution (e.g. a Summit Main lap detected as Jefferson).
export async function reassignSessionTrack(sessionId, trackId) {
  const res = await authFetch(`${BASE}/leaderboard/sessions/${encodeURIComponent(sessionId)}/reassign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ track_id: trackId }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// ── Similarity Lab (admin-only for now) ────────────────────────────────────

function appendWeights(params, weights) {
  Object.entries(weights || {}).forEach(([k, v]) => params.set(`w_${k}`, v));
}

export async function getSimilarityTracks(userId = null) {
  const params = new URLSearchParams();
  if (userId) params.set('user_id', userId);
  const res = await authFetch(`${BASE}/similarity/tracks?${params}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getSimilarityRadar(trackId, {
  userId = null, vehicleId = null, minPasses = null, overlayKeys = null,
  lapBasis = null,
} = {}) {
  const params = new URLSearchParams({ track_id: trackId });
  if (userId) params.set('user_id', userId);
  if (vehicleId != null) params.set('vehicle_id', vehicleId);
  if (minPasses) params.set('min_passes', minPasses);
  if (lapBasis) params.set('lap_basis', lapBasis);
  (overlayKeys || []).forEach(k => params.append('overlay_keys', k));
  const res = await authFetch(`${BASE}/similarity/radar?${params}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getSimilarDrivers(trackId, {
  userId = null, vehicleId = null, weights = null, minPasses = null, limit = null,
  lapBasis = null,
} = {}) {
  const params = new URLSearchParams({ track_id: trackId });
  if (userId) params.set('user_id', userId);
  if (vehicleId != null) params.set('vehicle_id', vehicleId);
  if (minPasses) params.set('min_passes', minPasses);
  if (limit) params.set('limit', limit);
  if (lapBasis) params.set('lap_basis', lapBasis);
  appendWeights(params, weights);
  const res = await authFetch(`${BASE}/similarity/similar_drivers?${params}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getSimilarityPasses(trackId, userId = null, vehicleId = null) {
  const params = new URLSearchParams({ track_id: trackId });
  if (userId) params.set('user_id', userId);
  if (vehicleId != null) params.set('vehicle_id', vehicleId);
  const res = await authFetch(`${BASE}/similarity/passes?${params}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getSimilarityNeighbors({
  sessionId, lapNumber, cornerId, scope = 'corner', otherDrivers = false,
  limit = 15, weights = null,
}) {
  const params = new URLSearchParams({
    session_id: sessionId, lap_number: lapNumber, corner_id: cornerId,
    scope, other_drivers: otherDrivers, limit,
  });
  appendWeights(params, weights);
  const res = await authFetch(`${BASE}/similarity/neighbors?${params}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// Corner insight (user-facing, viewer-authorized — works in share/guest mode):
// per-corner own-vs-faster-neighbor centroid shapes + gap + technique phrases.
// mode: 'field' (style-matched faster drivers, anonymous) | 'self' (own best).
export async function getCornerInsight(sessionId, mode = 'field') {
  const params = new URLSearchParams({ session_id: sessionId, mode });
  const res = await authFetch(`${BASE}/similarity/corner_insight?${params}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getCornerArchetypes(trackId, cornerId) {
  const params = new URLSearchParams({ track_id: trackId, corner_id: cornerId });
  const res = await authFetch(`${BASE}/similarity/corner_archetypes?${params}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// ── 3D ghost race ─────────────────────────────────────────────────────────
// laps: [{ session_id, lap_index, name?, car? }] — 1 to 4 entries.
export async function lap3dCompare(laps, fps = 30, corridor = null, fullLaps = false) {
  const body = { laps, fps };
  if (fullLaps) body.full_laps = true;
  if (corridor?.sessionId) {
    body.corridor_session_id = corridor.sessionId;
    body.corridor_scope = corridor.scope || 'session';
    body.corridor_lap_count = corridor.lapCount || 5;
    if (corridor.driverId) body.corridor_driver_id = corridor.driverId;
  }
  const res = await authFetch(`${BASE}/lap3d/compare`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function lap3dCorridorSources(sessionId) {
  const params = new URLSearchParams({ session_id: sessionId });
  const res = await authFetch(`${BASE}/lap3d/corridor-sources?${params}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function readNdjsonResult(response, onProgress) {
  if (!response.body) throw new Error('Scenery progress stream is unavailable');
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let result;

  const handleLine = (line) => {
    if (!line.trim()) return;
    const event = JSON.parse(line);
    if (event.type === 'progress') onProgress?.(event);
    else if (event.type === 'result') result = event.data;
    else if (event.type === 'error') throw new Error(event.message || 'Scenery build failed');
  };

  while (true) {
    const { value, done } = await reader.read();
    buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    lines.forEach(handleLine);
    if (done) break;
  }
  handleLine(buffer);
  if (!result) throw new Error('Scenery build ended without a result');
  return result;
}

// Aerial + elevation bake for a comparison's venue, plus per-lap pavement
// alignment. A cold venue can take minutes, so the endpoint streams real stage
// updates while provider downloads and image processing run.
export async function lap3dGround(
  laps,
  bbox,
  geometry = null,
  quality = 'balanced',
  { onProgress, signal } = {},
) {
  const res = await authFetch(`${BASE}/lap3d/ground-progress`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ laps, bbox, geometry, quality }),
    signal,
  });
  if (!res.ok) throw new Error(await res.text());
  return readNdjsonResult(res, onProgress);
}

// Turn any browser-side frame capture into an MP4 - the ghost race and the Track
// Difference ribbon both come through here. The route keeps its `lap3d` name
// because the endpoint is format-generic: it authorises nothing beyond being
// signed in, since the payload is pixels the client rendered from data it
// already had.
//
// Whatever the client managed to encode carries the right frames in the right
// order and no usable timing, so the server imposes `fps`: for `h264` (raw
// Annex-B out of WebCodecs) that is just a mux, for `webm` (the MediaRecorder
// fallback) it is a re-time plus transcode. `frames` is what the client believes
// it encoded, so the server can report back whether they all survived.
export async function exportCapturedVideo(blob, { fps, frames, filename, container }) {
  const form = new FormData();
  form.append('clip', blob, `capture.${container}`);
  form.append('fps', String(fps));
  form.append('frames', String(frames));
  form.append('filename', filename);
  form.append('container', container);
  const res = await authFetch(`${BASE}/lap3d/export`, { method: 'POST', body: form });
  if (!res.ok) throw new Error(await res.text());
  const encoded = Number(res.headers.get('X-Frames-Encoded')) || null;
  return { blob: await res.blob(), encoded };
}

// --- Video search (SimilarityLab) -------------------------------------------
// Proxied to VideoTrackRender, which holds the video index in its own database.
// The backend scopes every query to the caller's own sessions, so nothing here
// needs to pass session ids unless the user wants to narrow further.

export async function videoSearch(body) {
  const res = await authFetch(`${BASE}/video-search`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

/**
 * URL for one indexed frame.
 *
 * VTR returns paths relative to its thumbnail root; the backend serves them behind
 * the same ownership check as the search itself. The access token rides in the
 * query string because <img src> cannot set an Authorization header — the same
 * mechanism, for the same reason, as the SSE endpoints.
 *
 * Returns null when a hit has no thumbnail, so callers can render a placeholder
 * rather than an image that 404s.
 */
export function videoThumbUrl(relPath, userId = null) {
  if (!relPath) return null;
  const qs = new URLSearchParams({ token: getAccessToken() || '' });
  if (userId) qs.set('user_id', userId);
  const encoded = relPath.split('/').map(encodeURIComponent).join('/');
  return `${BASE}/video-search/thumbnail/${encoded}?${qs}`;
}

// Returns null on failure so the caller can hide the panel rather than error.
export async function videoSearchHealth(userId = null) {
  try {
    const qs = userId ? `?user_id=${encodeURIComponent(userId)}` : '';
    const res = await authFetch(`${BASE}/video-search/health${qs}`);
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

// --- Windows desktop account pairing --------------------------------------

export async function getDesktopPairingRequest(code) {
  const res = await authFetch(
    `${BASE}/desktop-pairing/requests/${encodeURIComponent(code)}`,
  );
  if (!res.ok) throw await responseError(res, 'Pairing request not found');
  return res.json();
}

export async function approveDesktopPairing(code) {
  const res = await authFetch(
    `${BASE}/desktop-pairing/requests/${encodeURIComponent(code)}/approve`,
    { method: 'POST' },
  );
  if (!res.ok) throw await responseError(res, 'Could not approve this Windows app');
  return res.json();
}

export async function getDesktopSyncConnection() {
  const res = await authFetch(`${BASE}/sync/connection`);
  if (!res.ok) throw await responseError(res, 'Could not read sync connection');
  return res.json();
}

export async function startDesktopSyncPairing(deviceName = null) {
  const res = await authFetch(`${BASE}/sync/connection/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ device_name: deviceName }),
  });
  if (!res.ok) throw await responseError(res, 'Could not start account connection');
  return res.json();
}

export async function pollDesktopSyncPairing() {
  const res = await authFetch(`${BASE}/sync/connection/poll`, { method: 'POST' });
  if (!res.ok) throw await responseError(res, 'Could not finish account connection');
  return res.json();
}

export async function disconnectDesktopSync() {
  const res = await authFetch(`${BASE}/sync/connection`, { method: 'DELETE' });
  if (!res.ok) throw await responseError(res, 'Could not disconnect hosted account');
}

export async function startDesktopSessionSync(sessionId) {
  const res = await authFetch(
    `${BASE}/sync/sessions/${encodeURIComponent(sessionId)}/uploads`,
    { method: 'POST' },
  );
  if (!res.ok) throw await responseError(res, 'Could not start session upload');
  return res.json();
}

export async function getDesktopSyncJob(jobId) {
  const res = await authFetch(`${BASE}/sync/jobs/${encodeURIComponent(jobId)}`);
  if (!res.ok) throw await responseError(res, 'Could not read session upload status');
  return res.json();
}

export async function planDesktopSyncQueue(sessionIds = null) {
  const res = await authFetch(`${BASE}/sync/queue/plan`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ session_ids: sessionIds }),
  });
  if (!res.ok) throw await responseError(res, 'Could not inspect local sessions');
  return res.json();
}

export async function startDesktopSyncQueue(
  sessionIds = null,
  syncMode = 'ready',
  restoreHosted = false,
) {
  const res = await authFetch(`${BASE}/sync/queue`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      session_ids: sessionIds,
      sync_mode: syncMode,
      restore_hosted: restoreHosted,
    }),
  });
  if (!res.ok) throw await responseError(res, 'Could not start the sync queue');
  return res.json();
}

export async function getDesktopSyncQueue() {
  const res = await authFetch(`${BASE}/sync/queue`);
  if (!res.ok) throw await responseError(res, 'Could not read the sync queue');
  return res.json();
}

async function updateDesktopSyncQueue(action) {
  const res = await authFetch(`${BASE}/sync/queue/${action}`, { method: 'POST' });
  if (!res.ok) throw await responseError(res, `Could not ${action} the sync queue`);
  return res.json();
}

export const pauseDesktopSyncQueue = () => updateDesktopSyncQueue('pause');
export const resumeDesktopSyncQueue = () => updateDesktopSyncQueue('resume');
export const retryDesktopSyncQueue = () => updateDesktopSyncQueue('retry');

// Admin: index an already-rendered session for video search, without re-rendering.
export async function indexSessionVideo(sessionId, syncVerified = true) {
  const res = await authFetch(`${BASE}/sessions/${sessionId}/index-video`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sync_verified: syncVerified }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// Video Studio uses the same authenticated transport as Analysis.
export async function studioRequest(path, { method = 'GET', body, signal } = {}) {
  const form = body instanceof FormData;
  const res = await authFetch(`${BASE}/video-studio${path}`, {
    method, signal,
    ...(body == null ? {} : { headers: form ? {} : { 'Content-Type': 'application/json' }, body: form ? body : JSON.stringify(body) }),
  });
  if (!res.ok) throw await responseError(res, 'Video Studio request failed');
  if (res.status === 204) return null;
  if (path.endsWith('/download')) return res.blob();
  return res.json();
}

// ── Issue reports ──────────────────────────────────────────────────────────

/** `report` is the JSON half ({category,title,body,page,url,session_id,context});
 *  `screenshot` an optional Blob. Multipart because of the image. */
export async function submitIssueReport(report, screenshot = null) {
  const form = new FormData();
  form.append('payload', JSON.stringify(report));
  if (screenshot) form.append('screenshot', screenshot, 'screenshot.jpg');
  const res = await authFetch(`${BASE}/issues`, { method: 'POST', body: form });
  if (res.status === 429) throw new Error('Too many reports in the last hour — please try again later');
  if (!res.ok) throw await responseError(res, 'Could not submit the report');
  return res.json();
}

export async function listMyIssues() {
  const res = await authFetch(`${BASE}/issues/mine`);
  if (!res.ok) throw await responseError(res, 'Could not load your reports');
  return res.json();
}

export async function listIssues({ status = null, limit = 50, offset = 0 } = {}) {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (status) params.set('status', status);
  const res = await authFetch(`${BASE}/issues?${params}`);
  if (!res.ok) throw await responseError(res, 'Could not load issues');
  return res.json();
}

export async function getIssue(issueId) {
  const res = await authFetch(`${BASE}/issues/${issueId}`);
  if (!res.ok) throw await responseError(res, 'Could not load the issue');
  return res.json();
}

/** The screenshot route is auth-gated, so an <img src> can't reach it —
 *  fetch it and hand back an object URL (caller revokes). */
export async function fetchIssueScreenshot(issueId) {
  const res = await authFetch(`${BASE}/issues/${issueId}/screenshot`);
  if (!res.ok) throw await responseError(res, 'Could not load the screenshot');
  return URL.createObjectURL(await res.blob());
}

export async function patchIssue(issueId, body) {
  const res = await authFetch(`${BASE}/issues/${issueId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await responseError(res, 'Could not update the issue');
  return res.json();
}

export async function deleteIssue(issueId) {
  const res = await authFetch(`${BASE}/issues/${issueId}`, { method: 'DELETE' });
  if (!res.ok) throw await responseError(res, 'Could not delete the issue');
}
